import { LitElement, html, css, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { resolveRadius } from "../helper/utils/size-resolver.js";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkySheetBackground = "primary" | "secondary" | "tertiary" | "solid" | "transparent";
export type SkySheetBlurLevel = "none" | "sm" | "md" | "lg";
export type SkySheetShadow = "none" | "sm" | "md" | "lg" | "inset";
export type SkySheetBorder = "none" | "sm" | "md" | "lg";

/** Preset tokens or any CSS length (e.g. `1rem`, `16px`). */
export type SkySheetRadius = string;

const SHEET_RADIUS_PRESETS: Record<string, string> = {
  none: "0",
  sm: "var(--sky-radius-primary)",
  md: "var(--sky-radius-secondary)",
  lg: "var(--sky-radius-tertiary)",
  xl: "var(--sky-radius-full)",
};

/** Preset map includes `xl`; arbitrary values pass through like `resolveRadius`. */
export function resolveSheetRadius(input: string): string {
  if (Object.prototype.hasOwnProperty.call(SHEET_RADIUS_PRESETS, input)) {
    return SHEET_RADIUS_PRESETS[input];
  }
  return resolveRadius(input);
}

/**
 * @element sky-sheet
 *
 * @summary Themed surface container with configurable background, blur, border, and shadow.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/sheet
 *
 * @slot - Content placed inside the sheet container.
 *
 * @property {SkySheetBackground} bg - Background style.
 * @property {SkySheetBlurLevel} blurLevel - Backdrop blur intensity.
 * @property {SkySheetShadow} shadow - Shadow style preset.
 * @property {SkySheetBorder} border - Border style preset.
 * @property {SkySheetRadius} radius - Preset (`none`, `sm`, `md`, `lg`, `xl`) or CSS length (`1rem`, `16px`).
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @Behavior
 * - Applies visual styles directly on `:host` for compact composition.
 * - Uses theme CSS variables for consistent glass and elevation tokens.
 * - Renders slot content without additional wrappers.
 *
 * @example
 * ```html
 * <sky-sheet bg="solid" blur-level="md" shadow="lg" border="md" radius="lg">
 *   <h3>Deployment Summary</h3>
 *   <p>All regional checks passed. Ready to promote to production.</p>
 * </sky-sheet>
 * ```
 * ```vue
 * <template>
 *   <sky-sheet bg="solid" blur-level="md" shadow="lg" border="md" radius="lg">
 *     <h3>Deployment Summary</h3>
 *     <p>All regional checks passed. Ready to promote to production.</p>
 *   </sky-sheet>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-sheet bg="solid" blur-level="md" shadow="lg" border="md" radius="lg">
 *       <h3>Deployment Summary</h3>
 *       <p>All regional checks passed. Ready to promote to production.</p>
 *     </sky-sheet>
 *   );
 * }
 * ```
 */
@customElement("sky-sheet")
export class SkySheet extends LitElement {

  @property({ type: String, reflect: true })
  bg: SkySheetBackground = "primary";

  @property({ type: String, reflect: true })
  blurLevel: SkySheetBlurLevel = "sm";

  @property({ type: String, reflect: true })
  shadow: SkySheetShadow = "sm";

  @property({ type: String, reflect: true })
  border: SkySheetBorder = "sm";

  @property({ type: String, reflect: true })
  radius: SkySheetRadius = "md";

  override connectedCallback(): void {
    super.connectedCallback();
    this._applyRadiusVar();
  }

  private _applyRadiusVar(): void {
    this.style.setProperty("--sheet-radius", resolveSheetRadius(this.radius));
  }

  /** After PresetController hostUpdate — paint with current radius. */
  protected override update(changed: PropertyValues): void {
    if (changed.size === 0 || changed.has("radius") || changed.has("preset")) {
      this._applyRadiusVar();
    }
    super.update(changed);
  }


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: block;
      box-sizing: border-box;
      font-family: var(--sky-font);
      width: 100%;
      height: 100%;
      padding: var(--sky-space-3, 12px);
      border-radius: var(--sheet-radius, var(--sky-radius-secondary));
    }

    /* background */
    :host([bg="primary"]) {
      background: var(--sky-glass-primary);
    }
    :host([bg="secondary"]) {
      background: var(--sky-glass-secondary);
    }
    :host([bg="tertiary"]) {
      background: var(--sky-glass-tertiary);
    }
    :host([bg="solid"]) {
      background: var(--sky-glass-solid);
      background-image: none;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
    :host([bg="transparent"]) {
      background: transparent;
      background-image: none;
    }

    :host([bg="primary"]:not([blurLevel="none"])),
    :host([bg="secondary"]:not([blurLevel="none"])),
    :host([bg="tertiary"]:not([blurLevel="none"])) {
      background-image: var(--sky-glass-surface);
    }

    :host([blurLevel="none"]) {
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
    :host([blurLevel="sm"]) {
      backdrop-filter: var(--sky-blur-primary) var(--sky-brightness-primary)
        var(--sky-saturation-primary);
      -webkit-backdrop-filter: var(--sky-blur-primary)
        var(--sky-brightness-primary) var(--sky-saturation-primary);
    }
    :host([blurLevel="md"]) {
      backdrop-filter: var(--sky-blur-secondary) var(--sky-brightness-secondary)
        var(--sky-saturation-secondary);
      -webkit-backdrop-filter: var(--sky-blur-secondary)
        var(--sky-brightness-secondary) var(--sky-saturation-secondary);
    }
    :host([blurLevel="lg"]) {
      backdrop-filter: var(--sky-blur-tertiary) var(--sky-brightness-tertiary)
        var(--sky-saturation-tertiary);
      -webkit-backdrop-filter: var(--sky-blur-tertiary)
        var(--sky-brightness-tertiary) var(--sky-saturation-tertiary);
    }

    /* shadow */
    :host([shadow="none"]) {
      box-shadow: none;
    }
    :host([shadow="sm"]) {
      box-shadow: var(--sky-box-shadow-inset), var(--sky-box-shadow-primary);
    }
    :host([shadow="md"]) {
      box-shadow: var(--sky-box-shadow-inset), var(--sky-box-shadow-secondary);
    }
    :host([shadow="lg"]) {
      box-shadow: var(--sky-box-shadow-inset), var(--sky-box-shadow-tertiary);
    }
    :host([shadow="inset"]) {
      box-shadow: var(--sky-box-shadow-inset);
    }

    /* border */
    :host([border="none"]) {
      border: none;
    }
    :host([border="sm"]) {
      border: var(--sky-border-primary);
    }
    :host([border="md"]) {
      border: var(--sky-border-secondary);
    }
    :host([border="lg"]) {
      border: var(--sky-border-tertiary);
    }
  `;

  override render() {
    return html`<slot></slot>`;
  }
}
